import React from 'react';
import { SingleSelect } from '../../../Controls/Select/SingleSelect';
import { useFilterField } from '../../hooks/useFilterField';
import { useFilterBarContext } from '../../state/FilterBarContext';
import type { SelectFilterConfig } from '../../types';

export interface SingleSelectFilterControlProps {
  name: string;
  /** Overrides config.placeholder. */
  placeholder?: string;
  /** Render a static label inside the control's prefix slot, surfacing the
   *  field name without relying on placeholder text (which disappears once a
   *  value is selected). Forwarded to antd's native `prefix` prop on
   *  SingleSelect (inherited from BaseSelect). */
  prefix?: React.ReactNode;
}

/**
 * Renders a SELECT field's control. Sibling of `MultiSelectFilterControl` —
 * `FilterItem` picks one or the other based on `config.type`.
 */
export const SingleSelectFilterControl: React.FC<SingleSelectFilterControlProps> = ({
  name,
  placeholder: placeholderOverride,
  prefix,
}) => {
  const { value, options, setValue, config, isLoading } = useFilterField(name);
  const { size } = useFilterBarContext();

  if (!config || config.type !== 'SELECT') return null;

  const cfg = config as SelectFilterConfig;
  const size_ = cfg.size ?? size;
  const placeholder = placeholderOverride ?? cfg.placeholder;
  const controlProps = (cfg.controlProps ?? {}) as Record<string, unknown>;
  // During LOADING_OPTIONS the option set is in flux — disable the control to
  // prevent the user from picking a stale option that would race the reload.
  const loadingDisabled = isLoading;

  return (
    <SingleSelect
      {...controlProps}
      options={options}
      value={(value as string | number | null) ?? undefined}
      onChange={(v) => setValue(v ?? null)}
      size={size_}
      placeholder={placeholder}
      prefix={prefix}
      loading={isLoading}
      disabled={loadingDisabled || (controlProps as { disabled?: boolean }).disabled}
      debounceTime={cfg.debounceMs}
      showSearch={cfg.searchable !== false}
      // Per-field clear via antd's native X, gated on `clearable` (non-clearable
      // fields must keep their value — enforced by config invariants).
      allowClear={cfg.clearable !== false}
    />
  );
};
